// File: src/components/game/AddGameModal.js
import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Spinner, Alert, Row, Col } from "react-bootstrap";
import axios from "axios";

function AddGameModal({ show, handleClose, onGameAdded, seasonId }) {
    const [formData, setFormData] = useState({
        game_type: "regular", // Default to regular
        location: "",
        scheduled_date: "",
        home_team: "",
        away_team: "",
    });

    const [locations, setLocations] = useState([]);
    const [teams, setTeams] = useState([]);
    const [gameTypes, setGameTypes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    // Fetch dropdown data when the modal opens
    useEffect(() => {
        if (!show) return;

        // Fetch locations
        axios.get("/api/leagues/locations/", {
            withCredentials: true,
        })
            .then((response) => setLocations(response.data))
            .catch((error) => console.error("Error fetching locations:", error));

        // Fetch teams for the season
        axios.get(`/api/teams/${seasonId}/teams/`, {
            withCredentials: true,
        })
            .then((response) => setTeams(response.data))
            .catch((error) => console.error("Error fetching teams:", error));

        // Fetch game types
        axios.get("/api/games/game_types/", {
            withCredentials: true,
        })
            .then((response) => setGameTypes(response.data))
            .catch((error) => console.error("Error fetching game types:", error));
    }, [show, seasonId]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));

        // If game type is changed to bye, clear away team
        if (name === 'game_type' && value === 'bye') {
            setFormData(prev => ({ ...prev, away_team: "" }));
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setLoading(true);
        setErrorMessage("");

        // Prepare data for submission
        const submitData = {
            ...formData,
            game_type: formData.game_type
        };

        // Remove away_team from data if it's a bye game
        if (formData.game_type === 'bye') {
            delete submitData.away_team;
        }

        axios.post(`/api/games/add-game/${seasonId}/`, submitData, {
            withCredentials: true,
        })
            .then((response) => {
                onGameAdded(response.data);
                setFormData({
                    game_type: "regular",
                    location: "",
                    scheduled_date: "",
                    home_team: "",
                    away_team: "",
                });
                handleClose();
            })
            .catch((error) => {
                console.error("Error adding game:", error);
                if (error.response && error.response.data) {
                    setErrorMessage(error.response.data.error || "Failed to add game.");
                } else {
                    setErrorMessage("An unexpected error occurred.");
                }
            })
            .finally(() => setLoading(false));
    };

    const isByeGame = formData.game_type === 'bye';
    const isPlayoffGame = formData.game_type === 'playoff';

    return (
        <Modal show={show} onHide={handleClose} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title>Add Game</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {errorMessage && <Alert variant="danger">{errorMessage}</Alert>}
                <Form onSubmit={handleSubmit}>
                    <Row className="mb-3">
                        <Col md={6}>
                            <Form.Group controlId="game_type">
                                <Form.Label>Game Type</Form.Label>
                                <Form.Control
                                    as="select"
                                    name="game_type"
                                    value={formData.game_type}
                                    onChange={handleChange}
                                    required
                                >
                                    {gameTypes.map((type) => (
                                        <option key={type.value} value={type.value}>
                                            {type.label}
                                        </option>
                                    ))}
                                </Form.Control>
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row className="mb-3">
                        <Col md={6}>
                            <Form.Group controlId="location">
                                <Form.Label>Location</Form.Label>
                                <Form.Control
                                    as="select"
                                    name="location"
                                    value={formData.location}
                                    onChange={handleChange}
                                >
                                    <option value="">Select a location</option>
                                    {locations.map((location) => (
                                        <option key={location.id} value={location.id}>
                                            {location.name}
                                        </option>
                                    ))}
                                </Form.Control>
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group controlId="scheduled_date">
                                <Form.Label>Scheduled Date</Form.Label>
                                <Form.Control
                                    type="datetime-local"
                                    name="scheduled_date"
                                    value={formData.scheduled_date}
                                    onChange={handleChange}
                                    required
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row className="mb-3">
                        <Col md={6}>
                            <Form.Group controlId="home_team">
                                <Form.Label>{isByeGame ? "Bye Team" : "Home Team"}</Form.Label>
                                <Form.Control
                                    as="select"
                                    name="home_team"
                                    value={formData.home_team}
                                    onChange={handleChange}
                                    required={!isPlayoffGame}
                                >
                                    <option value="">Select a {isByeGame ? "bye" : "home"} team</option>
                                    {isPlayoffGame && (
                                        <option value="tbd">TBD</option>
                                    )}
                                    {teams.map((team) => (
                                        <option key={team.id} value={team.id}>
                                            {team.name}
                                        </option>
                                    ))}
                                </Form.Control>
                            </Form.Group>
                        </Col>
                        {/* Away team field - only show for non-bye games */}
                        {!isByeGame && (
                            <Col md={6}>
                                <Form.Group controlId="away_team">
                                    <Form.Label>Away Team</Form.Label>
                                    <Form.Control
                                        as="select"
                                        name="away_team"
                                        value={formData.away_team}
                                        onChange={handleChange}
                                        required={!isByeGame && !isPlayoffGame}
                                    >
                                        <option value="">Select an away team</option>
                                        {isPlayoffGame && (
                                            <option value="tbd">TBD</option>
                                        )}
                                        {teams.map((team) => (
                                            <option key={team.id} value={team.id}>
                                                {team.name}
                                            </option>
                                        ))}
                                    </Form.Control>
                                </Form.Group>
                            </Col>
                        )}
                    </Row>
                    <div className="text-end">
                        <Button variant="secondary" onClick={handleClose} className="me-2">
                            Cancel
                        </Button>
                        <Button variant="primary" type="submit" disabled={loading}>
                            {loading ? <Spinner animation="border" size="sm" /> : "Add Game"}
                        </Button>
                    </div>
                </Form>
            </Modal.Body>
        </Modal>
    );
}

export default AddGameModal;
